<template>
  <div class="wave-report" v-loading="loading">
    <!-- 顶部信息 -->
    <div class="report-header">
      <div class="header-left">
        <el-button icon="el-icon-arrow-left" size="small" @click="goBack">返回概览</el-button>
        <h3 class="wave-title">{{ waveInfo.name || '波次报告' }}</h3>
      </div>
      <div class="header-stats">
        <span class="stat-item">
          <i class="el-icon-office-building"></i>
          门店 <b>{{ dealers.length }}</b> 家
        </span>
        <span class="stat-item">
          <i class="el-icon-document"></i>
          阶段 <b>{{ phases.length }}</b> 个
        </span>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="report-table-wrap">
      <el-table :data="filteredDealers" border stripe style="width: 100%" size="small"
        :header-cell-style="{ background: '#f5f7fa', color: '#303133', fontWeight: 600 }">
        <el-table-column type="index" label="序号" width="60" fixed></el-table-column>
        <el-table-column prop="dealerCode" label="门店代码" min-width="120" fixed>
          <template slot-scope="{ row }">
            <span class="dealer-code-cell">{{ row.DealerCode || row.dealerCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="dealerName" label="门店名称" min-width="160" fixed>
          <template slot-scope="{ row }">
            {{ row.DealerName || row.dealerName || '-' }}
          </template>
        </el-table-column>
        <!-- 动态阶段列：非考试阶段 -->
        <el-table-column v-for="phase in phases" :key="phase.phaseNo" min-width="140" align="center">
          <template slot="header">
            <span>{{ phase.phaseName }}</span>
            <el-tag v-if="phase.formType === 3" size="mini" type="warning" style="margin-left: 4px;">多级审核</el-tag>
          </template>
          <!-- 考试阶段：显示提交+一审/二审/三审 -->
          <template v-if="phase.formType === 3" slot-scope="{ row }">
            <div class="review-levels">
              <span :class="getStatusClass(getPhaseStatus(row, phase.phaseNo))">
                {{ getStatusText(getPhaseStatus(row, phase.phaseNo)) }}
              </span>
              <template v-if="getPhaseStatus(row, phase.phaseNo) === 'submitted' || getPhaseStatus(row, phase.phaseNo) === 'approved'">
                <span class="review-badges">
                  <span :class="getAuditStatus(row, phase.phaseNo) >= 1 ? 'badge-pass' : 'badge-wait'">一审</span>
                  <span :class="getAuditStatus(row, phase.phaseNo) >= 2 ? 'badge-pass' : 'badge-wait'">二审</span>
                  <span :class="getAuditStatus(row, phase.phaseNo) >= 3 ? 'badge-pass' : 'badge-wait'">三审</span>
                </span>
              </template>
            </div>
          </template>
          <!-- 普通阶段 -->
          <template v-else slot-scope="{ row }">
            <span :class="getStatusClass(getPhaseStatus(row, phase.phaseNo))">
              {{ getStatusText(getPhaseStatus(row, phase.phaseNo)) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 状态图例 -->
    <div class="legend">
      <span class="legend-item"><span class="legend-dot dot-default"></span>未提交</span>
      <span class="legend-item"><span class="legend-dot dot-warning"></span>待审核</span>
      <span class="legend-item"><span class="legend-dot dot-success"></span>已通过</span>
      <span class="legend-item"><span class="legend-dot dot-danger"></span>已驳回</span>
    </div>
  </div>
</template>

<script>
import { getWaveDealers, getWaveDetail } from 'index/api/project/wave'

export default {
  name: 'WaveReport',

  data() {
    return {
      loading: false,
      waveInfo: {},
      dealers: [],
      phases: []
    }
  },

  computed: {
    waveId() {
      return this.$route.params.waveId
    },
    filteredDealers() {
      return this.dealers
    }
  },

  mounted() {
    this.loadData()
  },

  methods: {
    async loadData() {
      if (!this.waveId) return
      this.loading = true
      try {
        // 并行加载波次详情 + 门店列表
        const [detailRes, dealerRes] = await Promise.all([
          getWaveDetail(this.waveId),
          getWaveDealers(this.waveId, { PageSize: 500 })
        ])

        // 波次详情
        const detail = detailRes?.data?.data || detailRes?.data || detailRes || {}
        this.waveInfo = {
          name: detail.Name || detail.name || ''
        }

        // 解析阶段信息（从 Phases 字段获取）
        const rawPhases = detail.Phases || detail.phases || []
        this.phases = rawPhases.map((p, i) => ({
          phaseNo: parseInt(p.phaseNo || p.PhaseNo) || i + 1,
          phaseName: p.phaseName || p.PhaseName || `阶段${i + 1}`,
          formId: p.formId || p.FormId || '',
          formType: parseInt(p.formType || p.FormType) || 0
        }))

        // 门店列表（含 PhaseStatuses）
        const dealerData = dealerRes?.data?.data || dealerRes?.data || dealerRes || {}
        this.dealers = dealerData.Rows || dealerData.rows || dealerData.Records || dealerData.records || []
      } catch (e) {
        console.error('加载波次报告失败', e)
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },


    getPhaseStatus(dealer, phaseNo) {
      const statuses = dealer.PhaseStatuses || dealer.phaseStatuses || []
      const ps = statuses.find(s => s.PhaseNo === phaseNo || s.phaseNo === phaseNo)
      return ps ? (ps.Status || ps.status || 'not_started') : 'not_started'
    },

    getAuditStatus(dealer, phaseNo) {
      const statuses = dealer.PhaseStatuses || dealer.phaseStatuses || []
      const ps = statuses.find(s => s.PhaseNo === phaseNo || s.phaseNo === phaseNo)
      return ps ? (ps.AuditStatus ?? ps.auditStatus ?? 0) : 0
    },

    getStatusText(status) {
      const map = {
        'not_started': '未提交',
        'in_progress': '填写中',
        'submitted': '待审核',
        'approved': '已通过',
        'rejected': '已驳回',
        'review1': '一审✓',
        'review2': '二审✓',
        'review3': '三审✓'
      }
      return map[status] || status
    },

    getStatusClass(status) {
      const map = {
        'not_started': 'status-default',
        'in_progress': 'status-info',
        'submitted': 'status-warning',
        'approved': 'status-success',
        'rejected': 'status-danger',
        'review1': 'status-success',
        'review2': 'status-success',
        'review3': 'status-success'
      }
      return map[status] || 'status-default'
    },

    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
.wave-report {
  padding: 0;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.wave-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.header-stats {
  display: flex;
  gap: 24px;
}

.stat-item {
  font-size: 13px;
  color: #64748b;
  i { margin-right: 4px; }
  b { color: #1e293b; font-size: 15px; }
}

.report-table-wrap {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}

.dealer-code-cell {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  color: #475569;
}

/* 状态标签 */
.status-default { color: #94a3b8; }
.status-info { color: #3b82f6; }
.status-warning { color: #f59e0b; font-weight: 500; }
.status-success { color: #10b981; font-weight: 500; }
.status-danger { color: #ef4444; font-weight: 500; }

/* 图例 */
.legend {
  display: flex;
  gap: 24px;
  margin-top: 16px;
  padding: 12px 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.dot-default { background: #cbd5e1; }
.dot-warning { background: #f59e0b; }
.dot-success { background: #10b981; }
.dot-danger { background: #ef4444; }

/* 审核级别 */
.review-levels {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.review-badges {
  display: flex;
  gap: 4px;
  span {
    font-size: 11px;
    padding: 1px 6px;
    border-radius: 3px;
  }
}

.badge-pass {
  background: #ecfdf5;
  color: #10b981;
  font-weight: 500;
}

.badge-wait {
  background: #f1f5f9;
  color: #94a3b8;
}
</style>
